import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
interface Option {
  value: string;
  label: string;
}
interface SearchableSelectProps {
  label?: string;
  value?: string;
  onChange: (e: {
    target: {
      value: string;
      name?: string;
    };
  }) => void;
  options: Option[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
  description?: string;
  fullWidth?: boolean;
}
export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Seleziona...',
  error,
  disabled = false,
  required = false,
  name,
  className = '',
  description,
  fullWidth = true
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find(opt => opt.value === value);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleSelect = (optionValue: string) => {
    onChange({
      target: {
        value: optionValue,
        name
      }
    });
    setIsOpen(false);
    setSearchTerm('');
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({
      target: {
        value: '',
        name
      }
    });
    setSearchTerm('');
  };
  return <div ref={containerRef} className={`${fullWidth ? 'w-full' : ''} flex flex-col gap-1.5`} data-id="element-1145">
      {label && <label className="block text-sm font-medium text-slate-700" data-id="element-1146">
          {label} {required && <span className="text-red-500" data-id="element-1147">*</span>}
        </label>}
      <div className="relative" data-id="element-1148">
        <div onClick={() => !disabled && setIsOpen(!isOpen)} className={`
            relative flex items-center w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 
            pl-4 pr-10 py-2.5 text-sm transition-all duration-200 cursor-pointer
            ${isOpen ? 'border-seform-orange bg-white ring-0' : 'hover:border-slate-300'}
            ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}
            ${error ? 'border-red-300 focus:border-red-500' : ''}
            ${className}
          `} data-id="element-1149">
          <span className={`block truncate ${!selectedOption ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-1150">
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1" data-id="element-1151">
            {selectedOption && !disabled && <div onClick={handleClear} className="p-1 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1152">
                <X className="h-3 w-3" data-id="element-1153" />
              </div>}
            <div className="pointer-events-none text-slate-400" data-id="element-1154">
              <ChevronDown className="h-4 w-4" data-id="element-1155" />
            </div>
          </div>
        </div>

        {isOpen && !disabled && <div className="absolute z-50 mt-1 w-full rounded-xl bg-white shadow-lg border border-slate-100 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100" data-id="element-1156">
            <div className="px-2 py-2 border-b border-slate-50" data-id="element-1157">
              <div className="relative" data-id="element-1158">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400" data-id="element-1159">
                  <Search className="h-3.5 w-3.5" data-id="element-1160" />
                </div>
                <input ref={inputRef} type="text" className="block w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 py-1.5 text-xs focus:border-seform-orange focus:ring-0 focus:outline-none" placeholder="Cerca..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus onClick={e => e.stopPropagation()} data-id="element-1161" />
              </div>
            </div>
            <div className="max-h-60 overflow-auto custom-scrollbar" data-id="element-1162">
              {filteredOptions.length > 0 ? filteredOptions.map(opt => <div key={opt.value} onClick={() => handleSelect(opt.value)} className={`
                      px-4 py-2 text-sm cursor-pointer transition-colors
                      ${value === opt.value ? 'bg-orange-50 text-seform-orange font-medium' : 'text-slate-700 hover:bg-slate-50'}
                    `} data-id="element-1163">
                    {opt.label}
                  </div>) : <div className="px-4 py-3 text-xs text-slate-400 text-center" data-id="element-1164">
                  Nessun risultato trovato
                </div>}
            </div>
          </div>}
      </div>
      {description && !error && <p className="text-xs text-slate-500" data-id="element-1165">{description}</p>}
      {error && <p className="text-xs text-red-600 font-medium" data-id="element-1166">{error}</p>}
    </div>;
}